import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Input,
  InputLabel,
  LinearProgress,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { red } from '@mui/material/colors';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { ICategory } from '../../../../shared-interfaces/Notation/ICategory';
import CategoryService from '../Services/CategoryService';

export interface AddNewCategoryDialogProps {
  open: boolean;
  canEnable: boolean;
  onCreate: (newCategory: ICategory) => void;
  onClose: () => void;
}

const useStyles = makeStyles({
  required: {
    color: red[800],
    fontSize: '12px',
  },
});

const AddNewCategoryDialog = (props: AddNewCategoryDialogProps) => {
  const classes = useStyles();
  const { onClose, open, canEnable, onCreate } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [newCategoryName, setNewCategoryName] = useState('');
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleClose = () => {
    setNewCategoryName('');
    setChecked(false);
    onClose();
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    CategoryService.createCategory({
      name: newCategoryName,
      isActive: checked,
    } as ICategory)
      .then(c => {
        onCreate(c);
        handleClose();
        enqueueSnackbar('Success!', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed', { variant: 'error' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="add-category-title"
      open={open}
    >
      <DialogTitle id="add-category-title">Add New Category</DialogTitle>
      <form>
        <DialogContent>
          <DialogContentText>
            Give the new category a name and optionally set it as active.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel htmlFor="category-name">
              <span className={classes.required}>*</span> Category Name
            </InputLabel>
            <Input
              id="category-name"
              name="Name"
              value={newCategoryName}
              onChange={event => setNewCategoryName(event.target.value)}
              fullWidth
              required
              error={newCategoryName.length > 30}
            />
            {newCategoryName.length > 30 && (
              <FormHelperText className={classes.required}>
                Enter a category name of 30 characters or less
              </FormHelperText>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleChange}
                  name="isActive"
                  color="primary"
                  disabled={!canEnable}
                />
              }
              label="Active"
            />
            {!canEnable && (
              <FormHelperText style={{ fontSize: '12px' }}>
                Only up to 30 categories can be active at once.
              </FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={
              !newCategoryName || newCategoryName.length > 30 || isSubmitting
            }
          >
            Save
          </Button>
        </DialogActions>
        {isSubmitting && <LinearProgress />}
      </form>
    </Dialog>
  );
};

export default AddNewCategoryDialog;
